import { useCallback, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { type Item, RenderVariant, SegmentationTargets } from '../../types';

export type useAudienceSegmentationProps = {
	audienceSegmentationGroupIds?: string[];
	cards: Item[];
	isCompanyHub: boolean;
	isInViewMode: boolean;
	isSegmentationShown: boolean;
	isTargetAudience: (targetGroupIds: string[] | undefined) => boolean;
	renderVariant: RenderVariant;
	segmentationTarget?: SegmentationTargets;
};

type ReturnType = {
	filteredCards: Item[];
	getShouldShowSegmentationMessage: (audienceSegmentationGroupIds: string[] | undefined) => boolean;
};

export const useAudienceSegmentation = ({
	isCompanyHub,
	isSegmentationShown,
	isTargetAudience,
	cards,
	isInViewMode,
	segmentationTarget,
	audienceSegmentationGroupIds,
	renderVariant,
}: useAudienceSegmentationProps): ReturnType => {
	const shouldApplySegmentation = useMemo(
		() => isCompanyHub && isSegmentationShown,
		[isCompanyHub, isSegmentationShown],
	);

	const filteredCards = useMemo(() => {
		const isAudienceSegmentationEnabled = isInViewMode && shouldApplySegmentation;
		const isDynamicOrManualWithAllTarget =
			renderVariant === RenderVariant.DYNAMIC ||
			(renderVariant === RenderVariant.MANUAL &&
				segmentationTarget === SegmentationTargets.ALL &&
				fg('company-hub-dynamic-cards-audience-segmentation'));

		if (!isAudienceSegmentationEnabled) {
			return cards;
		}
		if (isDynamicOrManualWithAllTarget) {
			return isTargetAudience(audienceSegmentationGroupIds) ? cards : [];
		}
		return cards.filter((card) => isTargetAudience(card.audienceSegmentationGroupIds));
	}, [
		isInViewMode,
		shouldApplySegmentation,
		segmentationTarget,
		cards,
		renderVariant,
		isTargetAudience,
		audienceSegmentationGroupIds,
	]);

	const getShouldShowSegmentationMessage = useCallback(
		(audienceSegmentationGroupIds: string[] | undefined) =>
			Boolean(
				shouldApplySegmentation &&
					audienceSegmentationGroupIds && // only show message if audience group is specified.
					isTargetAudience(audienceSegmentationGroupIds),
			),
		[shouldApplySegmentation, isTargetAudience],
	);

	return {
		filteredCards,
		getShouldShowSegmentationMessage,
	};
};
